import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { DeleteButton } from '@/components/DeleteConfirmDialog';
import { Euro, Edit, Plus, Calendar, Clock, Repeat, Play, Pause, Trash2, RefreshCw } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const KassenbuchPage = () => {
  const { API } = useContext(AuthContext);
  const [entries, setEntries] = useState([]);
  const [standorte, setStandorte] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('entries');
  
  // Dialoge
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showRecurringDialog, setShowRecurringDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingRecurring, setEditingRecurring] = useState(null);
  
  // Form Data für Einträge
  const [formData, setFormData] = useState({
    amount: '',
    location_id: '',
    note: '',
    entry_date: '',
    entry_time: ''
  });
  
  // Form Data für Daueraufträge
  const [recurringFormData, setRecurringFormData] = useState({
    title: '',
    amount: '',
    recurrence_type: 'MONTHLY',
    day_of_month: '1',
    month_of_year: '1',
    location_id: '',
    note: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
    fetchStandorte();
    fetchRecurring();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/kassenbuch`);
      setEntries(response.data || []);
    } catch (error) {
      if (error.response?.status >= 500) {
        toast.error('Serverfehler beim Laden des Kassenbuchs');
      } else if (error.response?.status === 403) {
        toast.error('Keine Berechtigung für Kassenbuch');
      }
      setEntries([]);
      console.error('Kassenbuch laden:', error.response?.status || error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStandorte = async () => {
    try {
      const response = await axios.get(`${API}/standorte`);
      setStandorte(response.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Standorte');
    }
  };

  const fetchRecurring = async () => {
    try {
      const response = await axios.get(`${API}/kassenbuch/recurring`);
      setRecurring(response.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Daueraufträge:', error);
      setRecurring([]);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/kassenbuch/manual`, {
        amount: parseFloat(formData.amount),
        location_id: formData.location_id || null,
        note: formData.note
      });
      toast.success('Umsatz eingetragen!');
      setShowAddDialog(false);
      setFormData({ amount: '', location_id: '', note: '', entry_date: '', entry_time: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Eintragen');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    const dateField = entry.created_at || entry.collected_at;
    let entryDate = '';
    let entryTime = '';
    
    if (dateField) {
      try {
        const date = new Date(dateField);
        entryDate = format(date, 'yyyy-MM-dd');
        entryTime = format(date, 'HH:mm');
      } catch (e) {
        console.error('Date parse error:', e);
      }
    }
    
    setFormData({
      amount: entry.amount.toString(),
      location_id: entry.location_id || '',
      note: entry.note || '',
      entry_date: entryDate,
      entry_time: entryTime
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        amount: parseFloat(formData.amount),
        location_id: formData.location_id || null,
        note: formData.note
      };
      
      // Datum/Uhrzeit nur hinzufügen wenn angegeben
      if (formData.entry_date && formData.entry_time) {
        const dateTimeStr = `${formData.entry_date}T${formData.entry_time}:00`;
        updateData.created_at = dateTimeStr;
      } else if (formData.entry_date) {
        updateData.created_at = `${formData.entry_date}T00:00:00`;
      }
      
      await axios.put(`${API}/kassenbuch/${editingEntry.id}`, updateData);
      toast.success('Eintrag aktualisiert!');
      setShowEditDialog(false);
      setEditingEntry(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (entry) => {
    try {
      await axios.delete(`${API}/kassenbuch/${entry.id}`);
      toast.success('Eintrag gelöscht!');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
      throw error;
    }
  };

  // --- Daueraufträge Handlers ---
  
  const handleAddRecurring = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/kassenbuch/recurring`, {
        title: recurringFormData.title,
        amount: parseFloat(recurringFormData.amount),
        recurrence_type: recurringFormData.recurrence_type,
        day_of_month: parseInt(recurringFormData.day_of_month),
        month_of_year: parseInt(recurringFormData.month_of_year),
        location_id: recurringFormData.location_id || null,
        note: recurringFormData.note,
        is_active: recurringFormData.is_active
      });
      toast.success('Dauerauftrag erstellt!');
      setShowRecurringDialog(false);
      resetRecurringForm();
      fetchRecurring();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleEditRecurring = (rec) => {
    setEditingRecurring(rec);
    setRecurringFormData({
      title: rec.title,
      amount: rec.amount.toString(),
      recurrence_type: rec.recurrence_type,
      day_of_month: (rec.day_of_month || 1).toString(),
      month_of_year: (rec.month_of_year || 1).toString(),
      location_id: rec.location_id || '',
      note: rec.note || '',
      is_active: rec.is_active
    });
    setShowRecurringDialog(true);
  };

  const handleUpdateRecurring = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/kassenbuch/recurring/${editingRecurring.id}`, {
        title: recurringFormData.title,
        amount: parseFloat(recurringFormData.amount),
        recurrence_type: recurringFormData.recurrence_type,
        day_of_month: parseInt(recurringFormData.day_of_month),
        month_of_year: parseInt(recurringFormData.month_of_year),
        location_id: recurringFormData.location_id || null,
        note: recurringFormData.note,
        is_active: recurringFormData.is_active
      });
      toast.success('Dauerauftrag aktualisiert!');
      setShowRecurringDialog(false);
      setEditingRecurring(null);
      resetRecurringForm();
      fetchRecurring();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const handleDeleteRecurring = async (rec) => {
    try {
      await axios.delete(`${API}/kassenbuch/recurring/${rec.id}`);
      toast.success('Dauerauftrag gelöscht!');
      fetchRecurring();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
      throw error;
    }
  };

  const handleToggleRecurring = async (rec) => {
    try {
      await axios.put(`${API}/kassenbuch/recurring/${rec.id}`, {
        is_active: !rec.is_active
      });
      toast.success(rec.is_active ? 'Dauerauftrag pausiert' : 'Dauerauftrag aktiviert');
      fetchRecurring();
    } catch (error) {
      toast.error('Fehler beim Ändern des Status');
    }
  };

  const handleExecuteRecurring = async () => {
    try {
      const response = await axios.post(`${API}/kassenbuch/recurring/execute`);
      if (response.data.created_entries?.length > 0) {
        toast.success(`${response.data.created_entries.length} Daueraufträge ausgeführt!`);
      } else {
        toast.info('Keine fälligen Daueraufträge');
      }
      fetchData();
      fetchRecurring();
    } catch (error) {
      toast.error('Fehler beim Ausführen der Daueraufträge');
    }
  };

  const resetRecurringForm = () => {
    setRecurringFormData({
      title: '',
      amount: '',
      recurrence_type: 'MONTHLY',
      day_of_month: '1',
      month_of_year: '1',
      location_id: '',
      note: '',
      is_active: true
    });
  };

  const getStandortName = (locationId) => {
    if (!locationId) return 'Sammelumsatz';
    const standort = standorte.find(s => s.id === locationId);
    return standort ? `${standort.name} (${standort.code})` : 'Unbekannt';
  };

  const formatRecurrenceLabel = (rec) => {
    if (rec.recurrence_type === 'MONTHLY') {
      return `Monatlich am ${rec.day_of_month}.`;
    } else {
      const months = ['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
      return `Jährlich am ${rec.day_of_month}. ${months[rec.month_of_year] || ''}`;
    }
  };

  const totalRevenue = entries.reduce((sum, entry) => sum + entry.amount, 0);

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div className="space-y-6 animate-slide-in pb-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Kassenbuch
          </h1>
          <p className="text-slate-600 text-sm sm:text-base">Umsätze und Daueraufträge verwalten</p>
        </div>
      </div>

      {/* Summary Card */}
      <Card className="p-4 sm:p-6 bg-gradient-to-r from-green-50 to-emerald-50">
        <div className="flex items-center space-x-4">
          <div className="bg-green-500 p-3 sm:p-4 rounded-xl flex-shrink-0">
            <Euro className="text-white" size={24} />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-slate-600 mb-1">Gesamtumsatz</p>
            <p className="text-2xl sm:text-4xl font-bold text-green-700">{totalRevenue.toFixed(2)} €</p>
            <p className="text-xs text-slate-500 mt-1">{entries.length} Einträge • {recurring.filter(r => r.is_active).length} aktive Daueraufträge</p>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="entries" className="flex items-center gap-2">
            <Euro size={16} />
            Einträge ({entries.length})
          </TabsTrigger>
          <TabsTrigger value="recurring" className="flex items-center gap-2">
            <Repeat size={16} />
            Daueraufträge ({recurring.length})
          </TabsTrigger>
        </TabsList>

        {/* Einträge Tab */}
        <TabsContent value="entries">
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Kassenbuch-Einträge</h2>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-600"
                data-testid="add-entry-btn"
              >
                <Plus size={20} />
                <span>Umsatz eintragen</span>
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Datum / Uhrzeit</th>
                    <th className="text-left py-3 px-4">Standort</th>
                    <th className="text-right py-3 px-4">Betrag</th>
                    <th className="text-left py-3 px-4">Notiz</th>
                    <th className="text-right py-3 px-4">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => {
                    let formattedDate = '-';
                    try {
                      const dateField = entry.created_at || entry.collected_at;
                      if (dateField) {
                        formattedDate = format(new Date(dateField), 'dd.MM.yyyy HH:mm', { locale: de });
                      }
                    } catch (e) {
                      formattedDate = String(entry.created_at || entry.collected_at || '-');
                    }
                    
                    return (
                      <tr key={entry.id} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-slate-400" />
                            {formattedDate}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {getStandortName(entry.location_id)}
                        </td>
                        <td className={`py-3 px-4 text-right font-bold ${
                          entry.amount < 0 ? 'text-red-700' : 'text-green-700'
                        }`}>
                          {entry.amount?.toFixed(2) || '0.00'} €
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          <div className="flex items-center gap-1">
                            {entry.type === 'dauerauftrag' && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">DA</span>
                            )}
                            {entry.note || '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-end gap-2">
                            <Button
                              onClick={() => handleEdit(entry)}
                              variant="outline"
                              size="sm"
                              data-testid={`edit-entry-${entry.id}`}
                            >
                              <Edit size={16} />
                            </Button>
                            <DeleteButton
                              onDelete={() => handleDelete(entry)}
                              title="Kassenbuch-Eintrag löschen"
                              itemName={`${entry.amount.toFixed(2)} €`}
                              description="Möchten Sie diesen Kassenbuch-Eintrag wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden."
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {entries.length === 0 && (
              <div className="text-center py-12">
                <Euro className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                <p className="text-lg font-medium text-slate-700 mb-2">Noch keine Buchungen vorhanden</p>
                <p className="text-slate-500">Das Kassenbuch wird automatisch gefüllt, wenn Automaten ausgezählt werden.</p>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Daueraufträge Tab */}
        <TabsContent value="recurring">
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Daueraufträge</h2>
              <div className="flex gap-2">
                <Button
                  onClick={handleExecuteRecurring}
                  variant="outline"
                  className="flex items-center gap-2"
                  title="Fällige Daueraufträge jetzt ausführen"
                  data-testid="execute-recurring-btn"
                >
                  <RefreshCw size={16} />
                  Jetzt ausführen
                </Button>
                <Button
                  onClick={() => {
                    setEditingRecurring(null);
                    resetRecurringForm();
                    setShowRecurringDialog(true);
                  }}
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600"
                  data-testid="add-recurring-btn"
                >
                  <Plus size={20} />
                  <span>Dauerauftrag anlegen</span>
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {recurring.map((rec) => (
                <div 
                  key={rec.id} 
                  className={`p-4 border rounded-lg ${rec.is_active ? 'bg-white' : 'bg-slate-50 opacity-60'}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{rec.title}</h3>
                        {!rec.is_active && (
                          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">Pausiert</span>
                        )}
                      </div>
                      <p className={`text-lg font-bold ${rec.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {rec.amount.toFixed(2)} €
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Repeat size={14} />
                          {formatRecurrenceLabel(rec)}
                        </span>
                        {rec.next_run_at && (
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            Nächste: {format(new Date(rec.next_run_at), 'dd.MM.yyyy', { locale: de })}
                          </span>
                        )}
                      </div>
                      {rec.note && <p className="text-sm text-slate-500 mt-1">{rec.note}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleToggleRecurring(rec)}
                        variant="outline"
                        size="sm"
                        title={rec.is_active ? 'Pausieren' : 'Aktivieren'}
                      >
                        {rec.is_active ? <Pause size={16} /> : <Play size={16} />}
                      </Button>
                      <Button
                        onClick={() => handleEditRecurring(rec)}
                        variant="outline"
                        size="sm"
                      >
                        <Edit size={16} />
                      </Button>
                      <DeleteButton
                        onDelete={() => handleDeleteRecurring(rec)}
                        title="Dauerauftrag löschen"
                        itemName={rec.title}
                        description="Bereits erzeugte Kassenbuch-Einträge bleiben erhalten. Nur zukünftige Buchungen werden gestoppt."
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {recurring.length === 0 && (
                <div className="text-center py-12">
                  <Repeat className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                  <p className="text-lg font-medium text-slate-700 mb-2">Keine Daueraufträge</p>
                  <p className="text-slate-500">Legen Sie wiederkehrende Buchungen wie Miete oder Abonnements an.</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Entry Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Umsatz eintragen</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <Label>Betrag (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                placeholder="0.00"
              />
            </div>
            <div>
              <Label>Standort (optional)</Label>
              <Select value={formData.location_id || "none"} onValueChange={(value) => setFormData({ ...formData, location_id: value === "none" ? "" : value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Kein Standort (Sammelumsatz)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Standort (Sammelumsatz)</SelectItem>
                  {standorte
                    .filter((standort) => standort.id && standort.id.trim() !== "")
                    .map((standort) => (
                      <SelectItem key={standort.id} value={standort.id}>
                        {standort.name} ({standort.code})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notiz (optional)</Label>
              <Textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="Notiz zum Umsatz..."
                rows={3}
              />
            </div>
            <Button type="submit" className="w-full">
              Eintragen
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog - MIT DATUM/UHRZEIT */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Umsatz bearbeiten</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <Calendar size={14} />
                  Datum
                </Label>
                <Input
                  type="date"
                  value={formData.entry_date}
                  onChange={(e) => setFormData({ ...formData, entry_date: e.target.value })}
                  data-testid="edit-entry-date"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <Clock size={14} />
                  Uhrzeit
                </Label>
                <Input
                  type="time"
                  value={formData.entry_time}
                  onChange={(e) => setFormData({ ...formData, entry_time: e.target.value })}
                  data-testid="edit-entry-time"
                />
              </div>
            </div>
            <div>
              <Label>Betrag (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                placeholder="0.00"
              />
            </div>
            <div>
              <Label>Standort (optional)</Label>
              <Select value={formData.location_id || "none"} onValueChange={(value) => setFormData({ ...formData, location_id: value === "none" ? "" : value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Kein Standort (Sammelumsatz)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Standort (Sammelumsatz)</SelectItem>
                  {standorte
                    .filter((standort) => standort.id && standort.id.trim() !== "")
                    .map((standort) => (
                      <SelectItem key={standort.id} value={standort.id}>
                        {standort.name} ({standort.code})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notiz (optional)</Label>
              <Textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="Notiz zum Umsatz..."
                rows={3}
              />
            </div>
            <Button type="submit" className="w-full">
              Speichern
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Recurring Entry Dialog */}
      <Dialog open={showRecurringDialog} onOpenChange={setShowRecurringDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRecurring ? 'Dauerauftrag bearbeiten' : 'Dauerauftrag anlegen'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={editingRecurring ? handleUpdateRecurring : handleAddRecurring} className="space-y-4">
            <div>
              <Label>Bezeichnung *</Label>
              <Input
                value={recurringFormData.title}
                onChange={(e) => setRecurringFormData({ ...recurringFormData, title: e.target.value })}
                required
                placeholder="z.B. Miete, Strom, Versicherung..."
                data-testid="recurring-title"
              />
            </div>
            <div>
              <Label>Betrag (€) *</Label>
              <Input
                type="number"
                step="0.01"
                value={recurringFormData.amount}
                onChange={(e) => setRecurringFormData({ ...recurringFormData, amount: e.target.value })}
                required
                placeholder="0.00 (negativ für Ausgaben)"
              />
              <p className="text-xs text-slate-500 mt-1">Negativ für Ausgaben (z.B. -500 für Miete)</p>
            </div>
            <div>
              <Label>Wiederholung *</Label>
              <Select 
                value={recurringFormData.recurrence_type} 
                onValueChange={(value) => setRecurringFormData({ ...recurringFormData, recurrence_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MONTHLY">Monatlich</SelectItem>
                  <SelectItem value="YEARLY">Jährlich</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tag im Monat *</Label>
                <Select 
                  value={recurringFormData.day_of_month} 
                  onValueChange={(value) => setRecurringFormData({ ...recurringFormData, day_of_month: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(28)].map((_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}.</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {recurringFormData.recurrence_type === 'YEARLY' && (
                <div>
                  <Label>Monat *</Label>
                  <Select 
                    value={recurringFormData.month_of_year} 
                    onValueChange={(value) => setRecurringFormData({ ...recurringFormData, month_of_year: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'].map((month, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div>
              <Label>Standort (optional)</Label>
              <Select 
                value={recurringFormData.location_id || "none"} 
                onValueChange={(value) => setRecurringFormData({ ...recurringFormData, location_id: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Kein Standort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Standort</SelectItem>
                  {standorte
                    .filter((standort) => standort.id && standort.id.trim() !== "")
                    .map((standort) => (
                      <SelectItem key={standort.id} value={standort.id}>
                        {standort.name} ({standort.code})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notiz (optional)</Label>
              <Textarea
                value={recurringFormData.note}
                onChange={(e) => setRecurringFormData({ ...recurringFormData, note: e.target.value })}
                placeholder="Zusätzliche Informationen..."
                rows={2}
              />
            </div>
            <Button type="submit" className="w-full">
              {editingRecurring ? 'Speichern' : 'Dauerauftrag erstellen'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KassenbuchPage;
